import React from "react";
import { useTranslation } from "react-i18next";

type BadgeType = "admin" | "moderator" | "vip" | "verified";

interface UserBadgeProps {
  type: BadgeType;
  className?: string;
}

/**
 * UserBadge - Admin/Moderator/VIP/Verified kullanıcı rozetleri
 * 
 * Kullanım:
 *   <UserBadge type="admin" />
 *   <UserBadge type="moderator" />
 *   <UserBadge type="vip" />
 */
export const UserBadge: React.FC<UserBadgeProps> = ({ type, className = "" }) => {
  const { t } = useTranslation();

  const config = {
    admin: {
      label: t("badge.admin"),
      icon: "👑",
      bg: "linear-gradient(to right, #f59e0b, #eab308)",
      textColor: "#fff",
      borderColor: "#fbbf24",
      glowColor: "0 0 10px rgba(251, 191, 36, 0.5)",
    },
    moderator: {
      label: t("badge.moderator"),
      icon: "🛡️",
      bg: "linear-gradient(to right, #6366f1, #a855f7)",
      textColor: "#fff",
      borderColor: "#818cf8",
      glowColor: "0 0 10px rgba(129, 140, 248, 0.5)",
    },
    vip: {
      label: t("badge.vip"),
      icon: "⭐",
      bg: "linear-gradient(to right, #ec4899, #f43f5e)",
      textColor: "#fff",
      borderColor: "#f472b6",
      glowColor: "0 0 10px rgba(244, 114, 182, 0.5)",
    },
    verified: {
      label: t("badge.verified"),
      icon: "✓",
      bg: "linear-gradient(to right, #3b82f6, #06b6d4)",
      textColor: "#fff",
      borderColor: "#60a5fa",
      glowColor: "0 0 10px rgba(96, 165, 250, 0.5)",
    },
  };

  const badgeConfig = config[type];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 8px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "500",
        border: `1px solid ${badgeConfig.borderColor}`,
        background: badgeConfig.bg,
        color: badgeConfig.textColor,
        boxShadow: `${badgeConfig.glowColor}, 0 4px 6px rgba(0,0,0,0.1)`,
        transition: "all 0.2s",
        cursor: "default",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = "scale(1.05)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
      className={className}
      title={badgeConfig.label}
    >
      <span style={{ fontSize: "10px" }} role="img" aria-label={badgeConfig.label}>
        {badgeConfig.icon}
      </span>
      <span style={{ lineHeight: "1" }}>{badgeConfig.label}</span>
    </span>
  );
};

/**
 * UserBadgeList - Bir kullanıcının tüm rozetlerini gösteren liste
 * 
 * Kullanım:
 *   <UserBadgeList user={user} />
 */
interface UserBadgeListProps {
  user: {
    isAdmin?: boolean;
    role?: "user" | "vip" | "moderator" | "admin";
    roles?: string[];
  };
  className?: string;
}

export const UserBadgeList: React.FC<UserBadgeListProps> = ({ user, className = "" }) => {
  const badges: BadgeType[] = [];

  // Admin badge (isAdmin field veya role="admin")
  if (user.isAdmin || user.role === "admin") {
    badges.push("admin");
  }

  // Moderator badge
  if (user.role === "moderator" || user.roles?.includes("moderator")) {
    badges.push("moderator");
  }

  // VIP badge
  if (user.role === "vip" || user.roles?.includes("vip")) {
    badges.push("vip");
  }

  // Verified badge (roles array'den)
  if (user.roles?.includes("verified")) {
    badges.push("verified");
  }

  if (badges.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        flexWrap: "wrap",
      }}
      className={className}
    >
      {badges.map((badgeType) => (
        <UserBadge key={badgeType} type={badgeType} />
      ))}
    </div>
  );
};
